import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Heart, Calendar, Infinity as InfinityIcon, CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TIER_META = {
  excalibur_annual:  { icon: Crown,        color: "bg-purple-900",  ring: "ring-amber-400", tag: "Most Prestigious" },
  excalibur_monthly: { icon: Crown,        color: "bg-purple-800",  ring: "ring-amber-300", tag: "Excalibur Monthly" },
  lifetime:  { icon: InfinityIcon, color: "bg-amber-500",   ring: "ring-purple-700", tag: "Best Long-Term Value" },
  annual:    { icon: Calendar,     color: "bg-purple-700",  ring: "ring-purple-200", tag: "Most Popular" },
  monthly:   { icon: Calendar,     color: "bg-slate-900",   ring: "ring-slate-200", tag: "Lowest Commitment" },
};

export default function Membership() {
  const { user, setUser } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);
  const [donation, setDonation] = useState("25");
  const [params] = useSearchParams();
  const [pollMsg, setPollMsg] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [liveMode, setLiveMode] = useState(true);

  const loadSubscription = () => {
    api.get("/payments/subscription").then(({ data }) => setSubscription(data)).catch(() => setSubscription(null));
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel auto-renew? You'll keep access until the end of the current billing period.")) return;
    try {
      await api.post("/payments/cancel-subscription");
      toast.success("Auto-renew canceled — you still have access until the period ends.");
      loadSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const resumeSubscription = async () => {
    try {
      await api.post("/payments/resume-subscription");
      toast.success("Auto-renew resumed");
      loadSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resume");
    }
  };

  const loadAll = () => {
    api.get("/payments/tiers").then(({ data }) => setTiers(data));
    api.get("/payments/history").then(({ data }) => setHistory(data));
    api.get("/payments/mode").then(({ data }) => setLiveMode(!!data.live)).catch(() => setLiveMode(true));
    loadSubscription();
  };
  useEffect(() => { loadAll(); }, []);

  // Poll after Stripe redirect
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setPollMsg("Confirming your payment…");
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPollMsg(null);
          toast.success(`Payment received: $${data.amount.toFixed(2)} (${data.purpose})`);
          loadAll();
          window.history.replaceState({}, "", "/app/membership");
          return;
        }
        if (data.status === "expired") { setPollMsg("Session expired."); return; }
        if (++attempts < 8) setTimeout(poll, 2000);
        else setPollMsg("Still processing — check history shortly.");
      } catch { setPollMsg("Could not verify payment."); }
    };
    poll();
  }, [params]);

  const subscribe = async (tierId) => {
    setBusy(tierId);
    try {
      const { data } = await api.post("/payments/subscribe", { tier_id: tierId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subscription setup failed");
      setBusy(null);
    }
  };

  const checkoutOneTime = async (body, key) => {
    setBusy(key);
    try {
      const { data } = await api.post("/payments/checkout", { ...body, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const tierOrder = ["excalibur_annual", "excalibur_monthly", "lifetime", "annual", "monthly"];
  const sortedTiers = [...tiers].sort((a, b) => tierOrder.indexOf(a.id) - tierOrder.indexOf(b.id));
  const hasActiveSubscription = subscription?.active;

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-brand-purple font-bold">Support CFAA</p>
        <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Membership & Dues</h1>
        <p className="mt-2 text-slate-500">Choose your tier or make a one-time donation. Recurring tiers auto-renew; cancel anytime.</p>
      </div>

      {user?.membership_tier && (
        <div className="rounded-2xl p-4 bg-purple-50 border border-purple-200 flex items-center gap-3" data-testid="current-membership">
          <CheckCircle2 className="w-5 h-5 text-purple-700" />
          <div className="flex-1">
            <p className="font-bold text-slate-900">Active: {user.membership_tier.toUpperCase()}</p>
            <p className="text-xs text-slate-600">
              {user.membership_until === "lifetime" ? "Lifetime member" : `Valid until ${new Date(user.membership_until).toLocaleDateString()}`}
            </p>
          </div>
        </div>
      )}

      {pollMsg && (
        <div className="rounded-2xl p-4 bg-amber-50 border border-amber-300 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-amber-700 animate-spin" />
          <p className="font-semibold text-slate-900">{pollMsg}</p>
        </div>
      )}

      {!liveMode && (
        <div className="rounded-2xl p-4 bg-slate-100 border border-slate-300" data-testid="sandbox-notice">
          <p className="font-black text-slate-900 text-sm">Payments are disabled in this environment.</p>
          <p className="mt-1 text-xs text-slate-600">Card charging is enabled only on the live production app. Visit the deployed CFAA app to pay your dues or donate.</p>
        </div>
      )}

      {/* Active subscription management */}
      {hasActiveSubscription && (
        <div className="rounded-3xl p-6 bg-white border-2 border-purple-200 shadow-sm" data-testid="subscription-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-700 text-white flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-black font-display">Your subscription</h3>
              <p className="mt-1 text-sm text-slate-500">
                Status: <span className="font-bold text-slate-900">{subscription.status}</span>
                {subscription.current_period_end && (
                  <> &middot; {subscription.cancel_at_period_end ? "Ends" : "Renews"} on{" "}
                    <span className="font-bold text-slate-900">
                      {new Date(subscription.current_period_end).toLocaleDateString()}
                    </span>
                  </>
                )}
              </p>
              <div className="mt-4 flex gap-3 flex-wrap">
                {subscription.cancel_at_period_end ? (
                  <Button
                    onClick={resumeSubscription}
                    data-testid="resume-sub-btn"
                    className="rounded-2xl h-11 px-5 font-bold bg-purple-700 hover:bg-purple-800 text-white btn-lift"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Resume auto-renew
                  </Button>
                ) : (
                  <Button
                    onClick={cancelSubscription}
                    data-testid="cancel-sub-btn"
                    variant="outline"
                    className="rounded-2xl h-11 px-5 font-bold border-red-300 text-red-700 hover:bg-red-50 btn-lift"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Cancel auto-renew
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {sortedTiers.map((t) => {
          const m = TIER_META[t.id] || { icon: Crown, color: "bg-purple-700", ring: "ring-purple-200", tag: "" };
          const Icon = m.icon;
          const isSubTier = t.kind === "subscription";
          const periodLabel = t.period === "once" ? "one-time" : t.period;
          return (
            <div key={t.id} data-testid={`tier-card-${t.id}`} className={`relative bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-sm hover:shadow-lg btn-lift flex flex-col`}>
              {m.tag && (
                <span className="absolute -top-2 left-6 bg-amber-500 text-slate-900 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                  {m.tag}
                </span>
              )}
              <div className={`w-12 h-12 rounded-2xl ${m.color} text-white flex items-center justify-center ring-4 ${m.ring}`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="mt-4 text-2xl font-black font-display">{t.name}</h3>
              <p className="mt-1 text-3xl font-black text-slate-900">
                ${t.amount.toFixed(2)}
                <span className="text-sm font-semibold text-slate-500"> / {periodLabel}</span>
              </p>
              {isSubTier && (
                <p className="mt-1 text-xs text-slate-500">Auto-renews, cancel anytime</p>
              )}
              <Button
                onClick={() => isSubTier ? subscribe(t.id) : checkoutOneTime({ tier_id: t.id }, t.id)}
                disabled={busy === t.id || !liveMode || (isSubTier && hasActiveSubscription)}
                data-testid={`pay-tier-${t.id}`}
                className="mt-6 w-full rounded-2xl h-11 font-bold bg-purple-700 hover:bg-purple-800 text-white btn-lift disabled:opacity-60"
              >
                {busy === t.id
                  ? "Redirecting…"
                  : !liveMode
                    ? "Available on live app"
                    : (isSubTier && hasActiveSubscription)
                      ? "Already subscribed"
                      : isSubTier
                        ? `Subscribe $${t.amount.toFixed(2)}/${t.period === "year" ? "yr" : "mo"}`
                        : `Pay $${t.amount.toFixed(2)}`}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="rounded-3xl p-6 bg-white border border-slate-100 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 text-slate-900 flex items-center justify-center">
            <Heart className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-black font-display">One-time donation</h3>
            <p className="text-sm text-slate-500">Support CFAA with any amount you choose ($1–$10,000).</p>
            <div className="mt-4 flex flex-wrap items-center gap-3 max-w-md">
              <span className="font-bold text-slate-900">$</span>
              <Input
                data-testid="donation-amount-input"
                type="number" min="1" max="10000" step="0.01"
                value={donation}
                onChange={(e) => setDonation(e.target.value)}
                className="w-32 h-11 rounded-xl border-slate-200"
              />
              <Button
                onClick={() => checkoutOneTime({ donation_amount: parseFloat(donation) }, "donation")}
                disabled={busy === "donation" || !donation || !liveMode}
                data-testid="pay-donation-btn"
                className="rounded-2xl h-11 px-5 font-bold bg-amber-500 hover:bg-amber-600 text-slate-900 btn-lift"
              >
                {busy === "donation" ? "Redirecting…" : !liveMode ? "Available on live app" : "Donate"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <h3 className="text-xl font-black font-display mb-3">Payment history</h3>
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.session_id} data-testid={`payment-row-${h.session_id}`} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-bold text-slate-900">${h.amount.toFixed(2)} <span className="text-xs font-normal text-slate-500">· {h.purpose}</span></p>
                  <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString()}</p>
                </div>
                <span className={`text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full ${h.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  {h.payment_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
